// Shared visual tokens for editorial chart primitives. Both ManaCurveChart
// and LifeChart pull stroke widths, opacities, gridline counts, font sizes,
// and dash patterns from this file so a future palette/typography tweak is
// one-file. Magic numbers in chart components are a smell — if you find one,
// promote it here.

/** Stroke widths (px in viewBox units). */
export const STROKE = {
  /** Non-focal series in line mode. */
  nonFocal: 1.5,
  /** Focal (single highlighted) series in line mode. */
  focal: 2,
  /** Hover guide / cursor. */
  cursor: 1,
  /** Y-axis dashed gridlines. */
  grid: 0.5,
  /** Solid baseline rule. */
  baseline: 1,
  /** Hairline border for tooltip and annotations. */
  hairline: 1,
} as const;

/** Opacities (0..1). */
export const OPACITY = {
  /** Y-grid dashed lines. */
  grid: 0.08,
  /** Solid baseline rule. */
  baseline: 0.16,
  /** Hover dashed cursor. */
  cursor: 0.25,
  /** Conditional area fill under a focal line. */
  areaFill: 0.18,
  /** Annotation hairline (mean-CMC etc). */
  annotation: 0.4,
  /** Hover dot halo. */
  haloRing: 1,
} as const;

/** Dash patterns (SVG stroke-dasharray strings). */
export const DASH = {
  /** Y-axis gridlines. */
  grid: "2 3",
  /** Hover vertical cursor. */
  cursor: "2 2",
} as const;

/** Typography (px sizes match SVG font-size which is unitless in viewBox). */
export const TYPE = {
  /** Axis tick labels. */
  axisLabel: 10,
  /** Series labels at line ends. */
  seriesLabel: 11,
  /** Tooltip body rows. */
  tooltipBody: 12,
  /** Tooltip header. */
  tooltipHeader: 11,
  /** Annotation labels (mean-CMC etc). */
  annotation: 9,
  /** Letter spacing for monospace axis labels. */
  trackingTight: -0.01,
  /** Letter spacing for uppercase eyebrows / annotations. */
  trackingWide: 0.04,
} as const;

/** Counts and sizing. */
export const SIZE = {
  /** Maximum number of horizontal gridlines (excluding the baseline). */
  maxGridlines: 3,
  /** Hover dot radius (px). */
  hoverDotRadius: 3,
  /** Hover dot white halo width (px). */
  hoverDotHalo: 1,
  /** Tooltip min-width (px). */
  tooltipMinWidth: 120,
  /** Tooltip vertical offset above hover point (px). */
  tooltipOffsetY: 10,
  /** End-label gap from canvas edge (px). */
  endLabelGap: 4,
  /** Annotation tick height (px). */
  annotationTickHeight: 8,
} as const;

/** Transition timings (ms). */
export const MOTION = {
  /** Toggle / mode switch. */
  toggle: 180,
  /** Hover fades. */
  hover: 120,
} as const;

/** Color tokens for non-data ink. Hex, not Tailwind, because they go into
 *  SVG attributes. Kept neutral so the editorial palette is the only color. */
export const INK = {
  /** Axis label fill (60% black-equivalent, on dark surface = light grey). */
  axisFill: "#9ca3af",
  /** Stronger fg for tooltip header. */
  fg: "#e4e4e7",
  /** Subtle fg for tooltip values + axis. */
  fgSubtle: "#a1a1aa",
  /** Annotation fill (50% on dark). */
  annotationFill: "#71717a",
  /** Hover dot halo color (matches surface so the dot reads punched-out). */
  haloFill: "#111113",
} as const;
